/**
 * 허브 발행/설치 오케스트레이션 — 결정 #23/#31(+#6/#18). 두 방향을 잇는다.
 *
 *  - **발행(publish*)**: 로컬 엔티티 → core 빌더로 발행 입력(교환 포맷 페이로드) →
 *    허브 업로드. 메타(제목·태그·등급)는 엔티티에서 끌어오고 폼 override로 덮는다.
 *  - **설치(installListing)**: 허브에서 페이로드를 받아 core 디코더로 도메인 엔티티로
 *    복원(새 id 부여 → 로컬 충돌 0) → 종류별로 저장. 평소 파일 가져오기와 동일한
 *    검증 경로(결정 #6/#17)를 그대로 탄다.
 *
 * core 인터페이스/함수로만 다룬다(결정 #10): 허브는 HubClient 포트, 저장은
 * StorageProvider, 변환은 core 순수 함수. web은 에셋 연결·게임 카드 등록 같은
 * 플랫폼 부수효과만 처리한다.
 */
import {
  buildCharacterPublish,
  buildGameCardPublish,
  buildLorebookPublish,
  buildPresetPublish,
  decodeListingPayload,
  type Character,
  type GameCard,
  type HubListing,
  type HubListingDetail,
  type Lorebook,
  type Preset,
  type PublishMetaOverride,
  type ShareMeta,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { hubClient } from "../platform/hub/index.js";
import { saveGameCard } from "./game-cards.js";
import { importAsset } from "../platform/assets/index.js";

// ---------------------------------------------------------------------------
// 발행(로컬 → 허브)
// ---------------------------------------------------------------------------

/** 캐릭터를 발행한다(결정 #23). 임베드 로어북이 있으면 함께 싣는다(character_book). */
export async function publishCharacter(
  character: Character,
  override: PublishMetaOverride = {},
  thumbnail?: Blob,
): Promise<HubListing> {
  const lorebook = character.lorebookId
    ? await storage.lorebooks.get(character.lorebookId)
    : undefined;
  const input = buildCharacterPublish(character, override, lorebook ?? undefined);
  return hubClient.publish({ ...input, ...(thumbnail ? { thumbnail } : {}) });
}

/** 로어북을 발행한다(결정 #23). */
export async function publishLorebook(
  book: Lorebook,
  override: PublishMetaOverride = {},
  thumbnail?: Blob,
): Promise<HubListing> {
  const input = buildLorebookPublish(book, override);
  return hubClient.publish({ ...input, ...(thumbnail ? { thumbnail } : {}) });
}

/** 프리셋을 발행한다(결정 #23). */
export async function publishPreset(
  preset: Preset,
  override: PublishMetaOverride = {},
): Promise<HubListing> {
  return hubClient.publish(buildPresetPublish(preset, override));
}

/** 게임 카드를 발행한다(결정 #23/#29). */
export async function publishGameCard(
  card: GameCard,
  override: PublishMetaOverride = {},
  thumbnail?: Blob,
): Promise<HubListing> {
  const input = buildGameCardPublish(card, override);
  return hubClient.publish({ ...input, ...(thumbnail ? { thumbnail } : {}) });
}

// ---------------------------------------------------------------------------
// 설치(허브 → 로컬)
// ---------------------------------------------------------------------------

/** 설치 결과 요약(알림 표시용). */
export interface InstallOutcome {
  kind: HubListing["kind"];
  title: string;
}

/**
 * 발행물을 받아 로컬에 설치한다(결정 #6/#23). listingId만 받아 페이로드를 직접
 * 다운로드하므로, 목록 화면에서 바로 "설치"를 호출할 수 있다.
 */
export async function installListing(listingId: string): Promise<InstallOutcome> {
  const detail = await hubClient.download(listingId);
  return installDetail(detail);
}

/**
 * 이미 받아둔 상세(페이로드 포함)를 설치한다(상세 화면에서 재다운로드 방지).
 * 종류별로 도메인 엔티티를 복원·저장하고, 출처 메타(ShareMeta)를 스탬프한다.
 */
export async function installDetail(detail: HubListingDetail): Promise<InstallOutcome> {
  const decoded = decodeListingPayload(detail.kind, detail.payload);

  switch (decoded.kind) {
    case "character": {
      let character = stampShare(decoded.character, detail);
      if (decoded.lorebook) {
        await storage.lorebooks.put(decoded.lorebook);
        character = { ...character, lorebookId: decoded.lorebook.id };
      }
      // 썸네일이 있으면 아바타로 연결(없으면 아바타 없이 저장).
      if (!character.avatarAssetId && detail.thumbnailUrl) {
        const asset = await fetchThumbnailAsset(detail.thumbnailUrl).catch(() => undefined);
        if (asset) character = { ...character, avatarAssetId: asset.id };
      }
      await storage.characters.put(character);
      return { kind: "character", title: character.name };
    }
    case "lorebook": {
      const book = stampShare(decoded.lorebook, detail);
      await storage.lorebooks.put(book);
      return { kind: "lorebook", title: book.name };
    }
    case "preset": {
      const preset = stampShare(decoded.preset, detail);
      await storage.presets.put(preset);
      return { kind: "preset", title: preset.name };
    }
    case "game-card": {
      const card = stampShareGame(decoded.card, detail);
      // saveGameCard가 저장 + 컴파일 + 레지스트리 등록까지 처리(결정 #29).
      await saveGameCard(card);
      return { kind: "game-card", title: card.definition.title };
    }
    default:
      throw new Error("알 수 없는 콘텐츠 종류입니다.");
  }
}

/**
 * 출처 메타(ShareMeta)를 엔티티에 스탬프한다(결정 #23). publicId로 같은 작품의
 * 업데이트를 추적할 수 있고, sourceUrl로 허브 출처를 남긴다. 기존 share가 있으면
 * 보존하되 허브 정보로 보강한다.
 */
function stampShare<T extends { share?: ShareMeta }>(
  entity: T,
  detail: HubListingDetail,
): T {
  return {
    ...entity,
    share: {
      ...entity.share,
      publicId: detail.id,
      author: detail.author.handle,
      authorId: detail.author.id,
      ...(detail.version ? { creatorVersion: detail.version } : {}),
      ...(detail.license ? { license: detail.license } : {}),
      ...(detail.tags.length ? { tags: detail.tags } : {}),
    },
  };
}

/** 게임 카드는 share가 GameCard 필드라 동일 로직을 카드 형태로 적용. */
function stampShareGame(card: GameCard, detail: HubListingDetail): GameCard {
  return stampShare(card, detail);
}

/** 썸네일 URL을 받아 콘텐츠 주소화 에셋으로 저장한다(결정 #18). */
async function fetchThumbnailAsset(url: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`썸네일 다운로드 실패(${res.status})`);
  const blob = await res.blob();
  return importAsset(blob);
}
